/**
 * Daily events (งานประจำวัน): what the townsfolk do together at set hours of the 60-minute day
 * (data/time.ts): the morning market, the incense hour at the shrine, the lion dance through the
 * city, the martial demonstration, the evening opera and the night lantern walk.
 *
 * Each event has its hours and one or more sites. A site is where it happens (a stage and the spot
 * the crowd gathers on, or a parade route), the extras it brings (by role: who they are, what they
 * do) and which of the zone's own townsfolk join in. The rules (systems/dailyEvents.ts) start and
 * end the events from the clock alone, so `dailyEventsAt(zone, hour)` says what is on for the HUD
 * as well as for the World.
 *
 * Spots are a landmark of the zone's map (`lm`, `plaza` is every map's plaza) or a point in region
 * metres (`at`: the city greybox, data/regions/yanyu.city.ts); a site whose spots are not on the
 * map (the authored village alone, tests) is skipped.
 *
 * What a role does is a beat after beat of NPC acts (data/npcActs.ts ids, `acts`), set through
 * `setAct` (systems/npcActs.ts); the zone's townsfolk join by their role there (`FOLK_ROLES`).
 */
import type { Activity } from './folk';
import type { NpcRole } from './npcActs';
import type { PassReward } from './pass';
import { START, type ZoneId } from './zones';

export type EventSpot =
  | { readonly lm: string; readonly dx?: number; readonly dy?: number }
  | { readonly at: readonly [number, number] };

/** Where an extra takes their place: on the stage, among the crowd, at a stall, or in the parade. */
export type Placement = 'stage' | 'crowd' | 'stall' | 'route';

export interface EventRole {
  readonly id: string;
  /** Shown over their head (Thai). */
  readonly title: string;
  /** Looks (render/looks ids), one picked per extra. */
  readonly looks: readonly string[];
  /** The NPC acts they play (data/npcActs.ts ids), one picked per beat. */
  readonly acts: readonly string[];
  /** Seconds a beat lasts. */
  readonly beat: readonly [number, number];
  /** The townsfolk work shown between acts (data/folk.ts `Activity`). */
  readonly work?: Activity;
  readonly lantern?: boolean;
  /** The townsperson whose manners (greetings, fright) they borrow. */
  readonly manner: string;
}

export interface EventSite {
  readonly id: string;
  readonly zone: ZoneId;
  /** Where (Thai), for the HUD. */
  readonly name: string;
  /** The performers' spot (or a parade's end), and the crowd's. */
  readonly stage: EventSpot;
  readonly crowd: EventSpot;
  /** Spread of the stage's and the crowd's places (m). */
  readonly stageR: number;
  readonly crowdR: number;
  /** Stalls (a market): vendors stand at these, facing the crowd. */
  readonly stalls?: readonly EventSpot[];
  /** A parade's way, start to end: the `route` extras walk it over the event, stopping at each point. */
  readonly route?: readonly EventSpot[];
  /** Where the extras come in from and go off to. */
  readonly enter: EventSpot;
  /** Extras by role and placement. */
  readonly cast: readonly { readonly role: string; readonly n: number; readonly at: Placement }[];
  /** The zone's townsfolk who join in (by role), as which role, from how far (m). */
  readonly join?: Partial<Readonly<Record<NpcRole, string>>>;
  readonly joinR?: number;
}

export interface DailyEventDef {
  readonly id: string;
  readonly name: string;
  readonly desc: string;
  /** From this hour to `to` (exclusive); `to` < `from` wraps past midnight. */
  readonly from: number;
  readonly to: number;
  readonly sites: readonly EventSite[];
  /** Seconds the player stays near (within `watchR` of the stage, or of a parade's head) for the reward. */
  readonly watch: number;
  readonly watchR: number;
  /** Paid once per event per day it is watched. */
  readonly reward: PassReward;
  /** Said by the performers to a player close by: lore, news, the way to a quest. */
  readonly barks: readonly string[];
  /** Logged with the reward: where the talk of the day points. */
  readonly hook: string;
}

export const EVENT_ROLES: Readonly<Record<string, EventRole>> = {
  vendor: {
    id: 'vendor',
    title: 'แม่ค้า',
    looks: ['teaSeller', 'farmerAunt', 'merchant', 'innkeeper'],
    acts: ['hawk', 'beckon', 'hawk', 'countCoins'],
    beat: [3, 6],
    work: 'call',
    manner: 'liu',
  },
  setup: {
    id: 'setup',
    title: 'คนตั้งแผง',
    looks: ['farmer', 'fisherOld'],
    acts: ['stall.setup', 'sweep'],
    beat: [4, 8],
    work: 'sweep',
    manner: 'niu',
  },
  shopper: {
    id: 'shopper',
    title: 'ชาวบ้าน',
    looks: ['farmerAunt', 'farmer', 'scholar', 'fisherOld', 'teaSeller', 'townKid'],
    acts: ['haggle', 'haggle', 'laugh', 'scratchHead', 'chat'],
    beat: [3, 7],
    manner: 'sun',
  },
  lion: {
    id: 'lion',
    title: 'คณะเชิดสิงโต',
    looks: ['red'],
    acts: ['lion.head'],
    beat: [6, 9],
    manner: 'zheng',
  },
  drummer: {
    id: 'drummer',
    title: 'มือกลอง',
    looks: ['monk', 'watchman'],
    acts: ['drum'],
    beat: [6, 9],
    manner: 'zheng',
  },
  gong: {
    id: 'gong',
    title: 'มือฆ้อง',
    looks: ['watchman', 'farmer'],
    acts: ['gong', 'gong'],
    beat: [3, 5],
    manner: 'zheng',
  },
  onlooker: {
    id: 'onlooker',
    title: 'ผู้ชม',
    looks: ['farmer', 'farmerAunt', 'scholar', 'townKid', 'teaSeller', 'fisherKid'],
    acts: ['watch.cheer', 'watch.clap', 'watch.clap', 'laugh', 'watch.cheer'],
    beat: [3, 7],
    manner: 'sun',
  },
  singer: {
    id: 'singer',
    title: 'นางเอกงิ้ว',
    looks: ['lilac', 'blissLady'],
    acts: ['opera.sing', 'opera.sing'],
    beat: [5, 8],
    manner: 'mist',
  },
  warrior: {
    id: 'warrior',
    title: 'พระเอกงิ้ว',
    looks: ['night', 'guardOfficer'],
    acts: ['opera.fight', 'baoquan'],
    beat: [4, 7],
    manner: 'moonwind',
  },
  monk: {
    id: 'monk',
    title: 'ผู้นำไหว้',
    looks: ['monk'],
    acts: ['incense', 'bow'],
    beat: [5, 8],
    manner: 'zheng',
  },
  pilgrim: {
    id: 'pilgrim',
    title: 'ผู้มาไหว้',
    looks: ['farmerAunt', 'fisherOld', 'farmer', 'teaSeller'],
    acts: ['incense', 'bow', 'incense'],
    beat: [4, 8],
    manner: 'hai',
  },
  fighter: {
    id: 'fighter',
    title: 'ศิษย์แสดงวรยุทธ์',
    looks: ['red', 'night', 'monk'],
    acts: ['demo.form', 'taichi', 'demo.form', 'demo.form'],
    beat: [4, 7],
    manner: 'skyblade',
  },
  lantern: {
    id: 'lantern',
    title: 'ผู้ถือโคม',
    looks: ['watchman', 'teaSeller', 'scholar', 'townKid', 'farmerAunt'],
    acts: ['lantern.carry', 'lantern.carry'],
    beat: [5, 9],
    lantern: true,
    manner: 'zheng',
  },
};

/** A point of the city in region metres (data/regions/yanyu.city.ts: CX 1450, CY 1650). */
const city = (x: number, y: number): EventSpot => ({ at: [x, y] });

const BASE_EVENTS: readonly DailyEventDef[] = [
  {
    id: 'incense',
    name: 'ไหว้ศาลยามรุ่ง',
    desc: 'ชาวบ้านจุดธูปไหว้ศาลเจ้าก่อนออกทะเลและลงนา',
    from: 5,
    to: 6,
    watch: 30,
    watchR: 14,
    reward: { cultivation: 40 },
    barks: [
      'จุดธูปสามดอก ขอให้ทะเลสงบ นาข้าวงาม',
      'ศาลนี้ตั้งมาตั้งแต่ก่อนศิลาตื่น ไหว้เถิดหลาน',
      'ควันธูปลอยตรงขึ้นฟ้า วันนี้ดี',
    ],
    hook: 'ข่าวลือหน้าศาล: หมาป่าเงาจันทร์ลงมาขุดคันนาอีกแล้ว ผู้เฒ่าหลิวกำลังหาจอมยุทธ์ช่วย',
    sites: [
      {
        id: 'village.shrine',
        zone: START,
        name: 'ศาลเจ้าหยานหยู',
        stage: { lm: 'shrine', dx: -3 },
        crowd: { lm: 'shrine', dx: 1.5 },
        stageR: 1,
        crowdR: 2.5,
        enter: { lm: 'plaza' },
        cast: [
          { role: 'monk', n: 1, at: 'stage' },
          { role: 'pilgrim', n: 3, at: 'crowd' },
        ],
        join: { fisher: 'pilgrim', farmer: 'pilgrim', child: 'pilgrim' },
        joinR: 80,
      },
    ],
  },
  {
    id: 'market',
    name: 'ตลาดเช้า',
    desc: 'แม่ค้าตั้งแผง ชาวบ้านมาต่อรองราคา',
    from: 6,
    to: 9,
    watch: 40,
    watchR: 18,
    reward: { gold: 15 },
    barks: [
      'ปลาสดจากท่าเรือเช้านี้! ถูกๆ',
      'ชาดอกไม้ร้อนๆ จิบก่อนไปฝึกวิชา',
      'สามอีแปะเท่านั้น ไม่ลดแล้วนะ',
      'เรือข้ามฟากเที่ยวเช้ามาพร้อมผ้าไหมจากเมืองใหญ่',
    ],
    hook: 'ข่าวลือในตลาด: พ่อค้าเกวียนถูกโจรภูเขาดักปล้นบนเนินทางตะวันตกเฉียงใต้',
    sites: [
      {
        id: 'village.plaza',
        zone: START,
        name: 'ลานหมู่บ้านหยานหยู',
        stage: { lm: 'plaza', dy: -4 },
        crowd: { lm: 'plaza', dy: 1 },
        stageR: 1,
        crowdR: 4,
        stalls: [
          { lm: 'plaza', dx: -6, dy: -4 },
          { lm: 'plaza', dx: -2, dy: -5 },
          { lm: 'plaza', dx: 2, dy: -5 },
          { lm: 'plaza', dx: 6, dy: -4 },
        ],
        enter: { lm: 'gate' },
        cast: [
          { role: 'vendor', n: 3, at: 'stall' },
          { role: 'setup', n: 1, at: 'stall' },
          { role: 'shopper', n: 4, at: 'crowd' },
        ],
        join: { fisher: 'vendor', farmer: 'vendor', innkeeper: 'shopper', child: 'shopper' },
        joinR: 80,
      },
      {
        id: 'city.market',
        zone: 'yanyu',
        name: 'ตลาดตะวันออก ลานศิลาเก้ากระบี่',
        stage: city(1690, 1482),
        crowd: city(1690, 1508),
        stageR: 2,
        crowdR: 14,
        stalls: [
          city(1650, 1474),
          city(1675, 1474),
          city(1700, 1474),
          city(1725, 1474),
          city(1650, 1501),
          city(1700, 1501),
          city(1730, 1501),
        ],
        enter: city(1640, 1560),
        cast: [
          { role: 'vendor', n: 6, at: 'stall' },
          { role: 'setup', n: 1, at: 'stall' },
          { role: 'shopper', n: 10, at: 'crowd' },
        ],
      },
    ],
  },
  {
    id: 'parade',
    name: 'แห่สิงโตรอบเมือง',
    desc: 'คณะเชิดสิงโตแห่ไปตามถนนหลวงสู่ลานศิลาใหญ่ หยุดเชิดทุกหัวมุม',
    from: 10,
    to: 12.5,
    watch: 45,
    watchR: 22,
    reward: { gold: 20, cultivation: 30 },
    barks: [
      'ตุ้ง ตุ้ง แช่! สิงโตมาแล้ว!',
      'สิงโตกินผักกาดหน้าร้านไหน ร้านนั้นรวยทั้งปี',
      'ปีที่ศิลาตื่น สิงโตเชิดถึงยอดเสาเลยนะ',
    ],
    hook: 'คณะสิงโตเล่าว่า: ในป่าสนทางตะวันออก หมาป่าหอนรับเสียงกลองทุกคืน',
    sites: [
      {
        id: 'city.parade',
        zone: 'yanyu',
        name: 'ถนนหลวง ลานศิลาเก้ากระบี่',
        stage: city(1590, 1658),
        crowd: city(1450, 1712),
        stageR: 2,
        crowdR: 12,
        route: [
          city(1450, 1880),
          city(1450, 1790),
          city(1450, 1708),
          city(1500, 1708),
          city(1500, 1658),
          city(1590, 1658),
        ],
        enter: city(1450, 1900),
        cast: [
          { role: 'lion', n: 2, at: 'route' },
          { role: 'drummer', n: 1, at: 'route' },
          { role: 'gong', n: 2, at: 'route' },
          { role: 'onlooker', n: 10, at: 'crowd' },
        ],
      },
    ],
  },
  {
    id: 'martial',
    name: 'สาธิตวรยุทธ์',
    desc: 'ศิษย์สำนักต่างๆ ออกมารำทวนกระบวนท่าให้ชาวบ้านชม',
    from: 14,
    to: 16,
    watch: 40,
    watchR: 16,
    reward: { cultivation: 60 },
    barks: [
      'ดูให้ดี โจมตีชนะท่าทำลาย ท่าทำลายชนะการตั้งรับ',
      'ฝึกเช้าเย็นสิบปี ยังไม่เท่าสู้จริงครั้งเดียว',
      'ใครอยากลองมือ ไปที่ลานฝึกได้ทุกเมื่อ',
    ],
    hook: 'ศิษย์คนหนึ่งกระซิบ: สุสานทางตะวันออกเฉียงใต้มีผีดิบเดินตอนกลางคืน ไม่มีใครกล้าไป',
    sites: [
      {
        id: 'village.yard',
        zone: START,
        name: 'ลานฝึกหยานหยู',
        stage: { lm: 'trainYard' },
        crowd: { lm: 'trainYard', dx: 4, dy: 3 },
        stageR: 2,
        crowdR: 2.5,
        enter: { lm: 'plaza' },
        cast: [
          { role: 'fighter', n: 2, at: 'stage' },
          { role: 'onlooker', n: 2, at: 'crowd' },
        ],
        join: { child: 'onlooker', farmer: 'onlooker', disciple: 'fighter' },
        joinR: 60,
      },
      {
        id: 'city.yard',
        zone: 'yanyu',
        name: 'ลานฝึกลานศิลา',
        stage: city(1279, 1747),
        crowd: city(1262, 1734),
        stageR: 5,
        crowdR: 8,
        enter: city(1235, 1690),
        cast: [
          { role: 'fighter', n: 4, at: 'stage' },
          { role: 'drummer', n: 1, at: 'stage' },
          { role: 'onlooker', n: 9, at: 'crowd' },
        ],
      },
    ],
  },
  {
    id: 'opera',
    name: 'งิ้วยามค่ำ',
    desc: 'คณะงิ้วเล่นเรื่องกระบี่ทั้งเก้า ที่โรงงิ้วริมน้ำ',
    from: 19,
    to: 21,
    watch: 50,
    watchR: 18,
    reward: { cultivation: 50, gold: 10 },
    barks: [
      'องก์ที่สาม: กระบี่เล่มที่เก้าหักกลางศิลา!',
      'กระบี่มีเก้า แต่ใครเล่าเคยนับเล่มที่สิบ…',
      'ปรบมือดังๆ หน่อยท่านผู้ชม!',
    ],
    hook: 'คนดูงิ้วเล่าต่อกันว่า: กระบี่เล่มที่สิบไม่เคยมีใครเห็น มีแต่ผู้เฒ่าหลิวที่ไม่ยอมพูดถึง',
    sites: [
      {
        id: 'city.opera',
        zone: 'yanyu',
        name: 'โรงงิ้วริมน้ำ',
        stage: city(1792, 1770),
        crowd: city(1781, 1770),
        stageR: 3,
        crowdR: 5,
        enter: city(1772, 1730),
        cast: [
          { role: 'singer', n: 1, at: 'stage' },
          { role: 'warrior', n: 2, at: 'stage' },
          { role: 'gong', n: 1, at: 'stage' },
          { role: 'onlooker', n: 12, at: 'crowd' },
        ],
      },
    ],
  },
  {
    id: 'lanterns',
    name: 'เดินโคมยามค่ำ',
    desc: 'ชาวบ้านถือโคมเดินเป็นขบวนรอบลาน ขอให้คืนนี้สงบ',
    from: 21,
    to: 23,
    watch: 40,
    watchR: 16,
    reward: { cultivation: 40 },
    barks: [
      'โคมหนึ่งดวง คนหนึ่งคน ไม่มีใครหลงทาง',
      'ดึกแล้ว ปิดประตูลงกลอน ระวังฟืนไฟ',
      'คืนเดือนมืดแบบนี้ ผีในสุสานชอบเดินเล่น',
    ],
    hook: 'ยามตีเกราะเตือน: คืนนี้มีเงาดำวิ่งบนหลังคา นักฆ่าจากหุบเขาเงาลงมาถึงตีนเขาแล้ว',
    sites: [
      {
        id: 'village.lanterns',
        zone: START,
        name: 'หมู่บ้านหยานหยู',
        stage: { lm: 'shrine', dx: 3 },
        crowd: { lm: 'plaza' },
        stageR: 1,
        crowdR: 4,
        route: [
          { lm: 'gate', dy: -3 },
          { lm: 'plaza', dy: 6 },
          { lm: 'plaza', dy: -4 },
          { lm: 'shrine', dx: 3 },
        ],
        enter: { lm: 'gate', dy: -3 },
        cast: [{ role: 'lantern', n: 4, at: 'route' }],
        join: { guard: 'lantern' },
        joinR: 120,
      },
      {
        id: 'city.lanterns',
        zone: 'yanyu',
        name: 'ลานศิลาใหญ่',
        stage: city(1400, 1610),
        crowd: city(1450, 1712),
        stageR: 3,
        crowdR: 12,
        route: [city(1500, 1700), city(1500, 1610), city(1400, 1610), city(1400, 1700), city(1500, 1700)],
        enter: city(1500, 1720),
        cast: [
          { role: 'lantern', n: 8, at: 'route' },
          { role: 'onlooker', n: 6, at: 'crowd' },
        ],
      },
    ],
  },
];

const flipSpot = (p: EventSpot): EventSpot => ('at' in p ? { at: [p.at[0], 3456 - p.at[1]] as const } : p);

/**
 * The city's sites again on แคว้นเก้ากระบี่ (the region mirrored, y -> 3456 - y), keyed to the new Yanyu's zone; the village
 * sites are the town's own already.
 */
export const DAILY_EVENTS: readonly DailyEventDef[] = BASE_EVENTS.map((d) => ({
  ...d,
  sites: [
    ...d.sites,
    ...d.sites
      .filter((s) => s.zone === 'yanyu')
      .map((s) => ({
        ...s,
        id: `${s.id}.jz`,
        zone: START,
        stage: flipSpot(s.stage),
        crowd: flipSpot(s.crowd),
        enter: flipSpot(s.enter),
        ...(s.stalls ? { stalls: s.stalls.map(flipSpot) } : {}),
        ...(s.route ? { route: s.route.map(flipSpot) } : {}),
      })),
  ],
}));

export const DAILY_EVENT_BY_ID: Readonly<Record<string, DailyEventDef>> = Object.fromEntries(
  DAILY_EVENTS.map((d) => [d.id, d]),
);

/** Whether an event is on at an hour (0–24). */
export function eventOn(d: DailyEventDef, hour: number): boolean {
  const h = ((hour % 24) + 24) % 24;
  return d.from < d.to ? h >= d.from && h < d.to : h >= d.from || h < d.to;
}

/** How far through an event the hour is (0 at its start … 1 at its end). */
export function eventProgress(d: DailyEventDef, hour: number): number {
  const len = (d.to - d.from + 24) % 24 || 24;
  const into = (((hour - d.from) % 24) + 24) % 24;
  return Math.min(1, into / len);
}

/** The events with a site in a zone. */
export const eventsOf = (zone: ZoneId): readonly DailyEventDef[] =>
  DAILY_EVENTS.filter((d) => d.sites.some((s) => s.zone === zone));

/** The zone's events on at an hour: what the HUD shows and the World stages. */
export const dailyEventsAt = (zone: ZoneId, hour: number): readonly DailyEventDef[] =>
  eventsOf(zone).filter((d) => eventOn(d, hour));

/** The zone's next event to start after an hour, and the hours until it does. */
export function nextDailyEvent(zone: ZoneId, hour: number): { event: DailyEventDef; inHours: number } | null {
  let best: { event: DailyEventDef; inHours: number } | null = null;
  for (const d of eventsOf(zone)) {
    const inHours = (((d.from - hour) % 24) + 24) % 24 || 24;
    if (!best || inHours < best.inHours) best = { event: d, inHours };
  }
  return best;
}

/** "06:00–09:00" for an event's hours. */
export function eventHours(d: DailyEventDef): string {
  const f = (h: number) =>
    `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`;
  return `${f(d.from)}–${f(d.to)}`;
}
